import Request from '@/services/util/request-service';
import {
  CourseBuilderData,
  SubunitBuilderData,
  UnitBuilderData,
  ActivityBuilderData,
} from 'course-types';

const CourseCreationService = {
  addUnit(course: CourseBuilderData): CourseBuilderData {
    const newUnit: UnitBuilderData = { name: '', description: '', subunits: [] };
    return { ...course, units: [...course.units, newUnit] };
  },

  addSubunit(course: CourseBuilderData, unitIndex: number): CourseBuilderData {
    const newSubunit: SubunitBuilderData = { name: '', activities: [] };
    const updatedUnits = course.units.map((unit, index) =>
      index === unitIndex ? { ...unit, subunits: [...unit.subunits, newSubunit] } : unit,
    );
    return { ...course, units: updatedUnits };
  },

  addActivity(
    course: CourseBuilderData,
    unitIndex: number,
    subunitIndex: number,
    activity: ActivityBuilderData,
  ): CourseBuilderData {
    const updatedUnits = course.units.map((unit, uIndex) => {
      if (uIndex === unitIndex) {
        const updatedSubunits = unit.subunits.map((subunit, sIndex) =>
          sIndex === subunitIndex
            ? { ...subunit, activities: [...subunit.activities, activity] }
            : subunit,
        );
        return { ...unit, subunits: updatedSubunits };
      }
      return unit;
    });
    return { ...course, units: updatedUnits };
  },

  updateCourseName(course: CourseBuilderData, name: string): CourseBuilderData {
    return { ...course, name };
  },

  updateCourseImage(course: CourseBuilderData, image: any): CourseBuilderData {
    return { ...course, image };
  },

  updateUnit(
    course: CourseBuilderData,
    unitIndex: number,
    field: string,
    value: string,
  ): CourseBuilderData {
    const updatedUnits = course.units.map((unit, index) =>
      index === unitIndex ? { ...unit, [field]: value } : unit,
    );
    return { ...course, units: updatedUnits };
  },

  updateSubunit(
    course: CourseBuilderData,
    unitIndex: number,
    subunitIndex: number,
    field: string,
    value: string,
  ): CourseBuilderData {
    const updatedUnits = course.units.map((unit, uIndex) => {
      if (uIndex === unitIndex) {
        const updatedSubunits = unit.subunits.map((subunit, sIndex) =>
          sIndex === subunitIndex ? { ...subunit, [field]: value } : subunit,
        );
        return { ...unit, subunits: updatedSubunits };
      }
      return unit;
    });
    return { ...course, units: updatedUnits };
  },

  removeUnit(course: CourseBuilderData, unitIndex: number): CourseBuilderData {
    const updatedUnits = course.units.filter((_, index) => index !== unitIndex);
    return { ...course, units: updatedUnits };
  },

  removeSubunit(
    course: CourseBuilderData,
    unitIndex: number,
    subunitIndex: number,
  ): CourseBuilderData {
    const updatedUnits = course.units.map((unit, uIndex) => {
      if (uIndex === unitIndex) {
        return { ...unit, subunits: unit.subunits.filter((_, sIndex) => sIndex !== subunitIndex) };
      }
      return unit;
    });
    return { ...course, units: updatedUnits };
  },

  isNextButtonEnabled(courseData: CourseBuilderData, currentStep: number): boolean {
    if (currentStep === 0 && courseData.name.trim() === '') {
      return false;
    }
    if (currentStep === 1 && courseData.units.length === 0) {
      return false;
    }
    if (currentStep === 1 && this.areUnitsBlank(courseData)) {
      return false;
    }
    if (currentStep === 2 && this.areSubunitsBlank(courseData)) {
      return false;
    }
    return true;
  },

  areUnitsBlank(courseData: CourseBuilderData): boolean {
    for (const unit of courseData.units) {
      if (unit.name.trim() === '') {
        return true;
      }
    }
    return false;
  },

  areSubunitsBlank(courseData: CourseBuilderData): boolean {
    for (const unit of courseData.units) {
      if (unit.subunits.length === 0) {
        return true;
      }
      for (const subunit of unit.subunits) {
        if (subunit.name.trim() === '') {
          return true;
        }
      }
    }
    return false;
  },

  async saveCourse(course: CourseBuilderData) {
    return await (await Request.post('course/createCourse', { course: course }))?.json();
  },

  async copyCourse(course: CourseBuilderData) {
    return await (await Request.post('course/copyCourse', { course: course }))?.json();
  },
};

export default CourseCreationService;
